import React, { useMemo, useCallback, Dispatch, SetStateAction } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useWatch, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTextAreaScrollStyles } from 'modules/notes/hooks';
import { useMessage } from 'modules/intl';
import { CharacterCounter } from 'modules/common/components/Counter/CharacterCounter';
import { useFormErrorDialogContext } from 'modules/notes/common';
import { SplitEntry, SPLIT_NOTE_FIELDS } from '../../types';
import { NOTE_MAX_CHAR_LIMIT } from '../../splitNoteSchema';
import { useFormChangeHandlers } from '../../hooks';

type SplitEntryDescriptionProps = {
  entry: SplitEntry;
  position: number;
  setIsFormError: Dispatch<SetStateAction<boolean>>;
};

const useStyles = makeStyles((theme) => ({
  note: {
    width: '100%',
    padding: theme.spacing(0),
    '& > div': {
      padding: theme.spacing(1.5, 1)
    },
    '&:hover $clearButton': {
      opacity: 1
    }
  },
  clearButton: {
    margin: theme.spacing(1),
    opacity: 0
  }
}));

const useCharCountStyles = makeStyles(() => ({
  root: {
    margin: 0
  }
}));

export const SplitEntryDescription: React.FC<SplitEntryDescriptionProps> = ({
  entry,
  position,
  setIsFormError
}: SplitEntryDescriptionProps) => {
  const classes = useStyles();
  const descriptionFormFieldName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.NOTE}`;
  const { handleDescriptionChange } = useFormChangeHandlers(position);
  const { errorContext, setErrorContext } = useFormErrorDialogContext();
  const classesScroll = useTextAreaScrollStyles();
  const placeHolder = useMessage('note.addNotePlaceholder');
  const inputProps = useMemo(
    () => ({
      'data-testid': 'add-note-comment-input'
    }),
    []
  );
  const {
    setValue,
    register,
    clearErrors,
    formState: { errors }
  } = useFormContext();

  const value = useWatch({
    name: descriptionFormFieldName,
    defaultValue: entry.description
  });

  const { onChange, ...rest } = register(descriptionFormFieldName);
  const error =
    errors &&
    errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`] &&
    errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`][position] &&
    Object.keys(errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`][position]).some(
      (key) => key === SPLIT_NOTE_FIELDS.NOTE
    ) &&
    errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`][position][
      `${SPLIT_NOTE_FIELDS.NOTE}`
    ];

  const characterCountClasses = useCharCountStyles();

  const clearNote = useCallback(() => {
    setValue(descriptionFormFieldName, undefined, {
      shouldDirty: true
    });
    clearErrors(descriptionFormFieldName);
    setErrorContext((prev) => {
      return { ...prev, ...{ noteExceeded: false } };
    });
    setIsFormError(false);
  }, [
    setValue,
    descriptionFormFieldName,
    clearErrors,
    setErrorContext,
    setIsFormError
  ]);

  const onDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleDescriptionChange(position)(event);
      const isError = Boolean(
        (event.target.value?.length ?? 0) > NOTE_MAX_CHAR_LIMIT
      );
      setErrorContext((prev) => {
        if (prev.noteExceeded !== isError) {
          return { ...prev, noteExceeded: isError };
        }
        return prev;
      });
      setIsFormError(isError);
      onChange(event);
    },
    [
      handleDescriptionChange,
      onChange,
      position,
      setErrorContext,
      setIsFormError
    ]
  );

  const InputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            className={classes.clearButton}
            data-testid={'input-clear-button'}
            onClick={clearNote}
            size="medium"
          >
            <ClearIcon fontSize={'small'} />
          </IconButton>

          <CharacterCounter
            text={value}
            hasError={errorContext.noteExceeded}
            classes={characterCountClasses}
            totalChar={NOTE_MAX_CHAR_LIMIT}
          />
        </InputAdornment>
      )
    }),
    [
      classes.clearButton,
      clearNote,
      value,
      errorContext.noteExceeded,
      characterCountClasses
    ]
  );

  const handleFocus = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const lengthOfInput = event.target.value.length;
      return event.target.setSelectionRange(lengthOfInput, lengthOfInput);
    },
    []
  );

  return (
    <TextField
      key={entry.key}
      data-testid={`split-note-comment-${position}`}
      className={classNames(classes.note, classesScroll.scrollBar)}
      autoFocus
      multiline
      maxRows={2}
      inputProps={inputProps}
      {...rest}
      placeholder={placeHolder}
      variant={'outlined'}
      defaultValue={entry.description}
      onChange={onDescriptionChange}
      error={Boolean(error) || errorContext.noteExceeded}
      InputProps={InputProps}
      onFocus={handleFocus}
    />
  );
};
